import {Mapper} from '@s-ui/domain'
import {toCapitalCase} from '@s-ui/js/lib/string'

/**
 * @implements Mapper
 */

export class TrackingEventsInstantOfferMapper extends Mapper {
  constructor({config}) {
    super()

    this._config = config

    this._C2B_ORIGINS = this._config.get('c2b').C2B_ORIGINS
    this._C2B_FLOW_BY_ORIGINS = this._config.get('tracking').C2B_FLOW_BY_ORIGINS
    this._CULTURE = this._config.get('DEFAULT_CULTURE')
    this._DEALER_TYPES = this._config.get('dealer').DEALER_TYPES
    this._FORM_NAME_C2B_ORIGINS = this._config.get('tracking').INSTANT_OFFER_FORM_NAMES_BY_C2B_ORIGINS
    this._fieldNameToTrackingIds = this._config.get('form').fieldNameToTrackingIds
    this._formFieldIds = this._config.get('form').formFieldIds
    this._formErrorTypes = this._config.get('form').formErrorTypes
    this._HIT_INFORMATIONS = this._config.get('tracking').HIT_INFORMATIONS
    this._TRACKING_EVENT_NAMES = this._config.get('tracking').TRACKING_EVENT_NAMES
    this._TRACKING_PAGE_ERRORS = this._config.get('tracking').TRACKING_PAGE_ERRORS
  }

  setParams({
    ad,
    appointment,
    calendarProvider,
    dealer,
    dealerList,
    errorType,
    fieldId,
    formErrors,
    hitInformation,
    hitInformation2,
    hitOrigin,
    instantOfferAppraisal,
    isDaySelected,
    isSlotSelected,
    origin,
    plate,
    postalCode,
    tabExpanded,
    whatsappConsentValue
  }) {
    this._ad = ad
    this._adId = this._ad?.id
    this._ioId = this._ad?.ioId
    this._appointment = appointment
    this._calendarProvider = calendarProvider
    this._dealer = dealer
    this._dealerList = dealerList
    this._errorType = errorType
    this._formErrors = formErrors
    this._fieldId = fieldId
    this._hitInformation = hitInformation
    this._hitInformation2 = hitInformation2
    this._hitOrigin = hitOrigin
    this._instantOfferAppraisal = instantOfferAppraisal
    this._isDaySelected = isDaySelected
    this._isSlotSelected = isSlotSelected
    this._origin = origin
    this._plate = plate
    this._postalCode = postalCode
    this._tabExpanded = tabExpanded
    this._whatsappConsentValue = whatsappConsentValue
    return this
  }

  _getAdProducts = ({adId}) =>
    adId
      ? [
          {
            id: this._adId,
            category: 'ads',
            price: 0,
            quantity: 0
          }
        ]
      : []

  _getC2bFlow = () =>
    this._C2B_FLOW_BY_ORIGINS?.[this._origin] || this._C2B_FLOW_BY_ORIGINS[this._C2B_ORIGINS?.PUBLISHED_AD]

  _getDateTrackingFormat = ({date: _date}) => {
    const date = new Date(_date.replace('Z', ''))

    const weekday = date.toLocaleDateString(this._CULTURE, {
      weekday: 'long'
    })

    const month = date.toLocaleDateString(this._CULTURE, {
      month: 'long'
    })

    const day = date.toLocaleDateString(this._CULTURE, {
      day: 'numeric'
    })

    const time = date.toLocaleTimeString(this._CULTURE, {
      hour: '2-digit',
      minute: '2-digit'
    })

    return {
      day: `${toCapitalCase(weekday)} ${day} de ${toCapitalCase(month)}`,
      hour: `${time}h`
    }
  }

  _getDealerCategory = ({dealer}) => {
    if (dealer.isRecommended) return 'c2bdealerrecomendado'
    if (dealer.type === this._DEALER_TYPES.ONLINE) return 'c2bdealeronline'
    return 'c2bdealer'
  }

  _getDealerProduct = ({category, dealer, listPosition}) => {
    const dealerProduct = {
      id: `dealer${dealer.id}`,
      category: category ?? this._getDealerCategory({dealer}),
      price: 0,
      quantity: 0
    }
    if (listPosition) dealerProduct.list_position = listPosition
    return dealerProduct
  }

  mapAdInsertionIOAddToCalendar = event => {
    const c2bFlow = this._getC2bFlow()
    return {event, data: {hit_information: this._calendarProvider, ...(c2bFlow && {c2b_flow: c2bFlow})}}
  }

  mapAdInsertionIOButtonClicked = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        products: this._getAdProducts({adId: this._adId}),
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  _getTrackingErrorData = ({formErrors}) => {
    const formFields = Object.keys(formErrors).reduce((acc, fieldKey) => {
      const fieldError = formErrors[fieldKey]
      if (!fieldError.length) return acc

      const fieldErrorId = this._fieldNameToTrackingIds[fieldKey].property
      return !acc ? fieldErrorId : `${acc}:${fieldErrorId}`
    }, '')

    return formFields
  }

  mapAdInsertionIOCarDetailsSuccess = event => {
    return {
      event,
      data: {
        hit_information: this._hitInformation,
        hit_information2: this._hitInformation2
      }
    }
  }

  mapAdInsertionIOCarDetailsError = event => {
    const c2bFlow = this._getC2bFlow()
    const data = {
      form_fields: this._getTrackingErrorData({formErrors: this._formErrors}),
      page_error: this._formErrorTypes.fieldError,
      ...(c2bFlow && {c2b_flow: c2bFlow})
    }

    if (this._formErrors[this._formFieldIds.plate]?.length) {
      data.hit_information = !this._plate ? this._HIT_INFORMATIONS.EMPTY_PLATE : this._HIT_INFORMATIONS.WRONG_PLATE
    }

    return {event, data}
  }

  mapAdInsertionIOCarDetailsViewed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        channel: 'ad post',
        page_category_level1: 'oferta instantanea',
        page_name: 'datos del coche',
        page_type: 'form',
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIOConfirmationFaqExpanded = event => {
    const c2bFlow = this._getC2bFlow()
    const data = {hit_information: this._tabExpanded, ...(c2bFlow && {c2b_flow: c2bFlow})}
    if (this._adId) data.products = this._getAdProducts({adId: this._adId})
    return {event, data}
  }

  mapAdInsertionIOConfirmationViewed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        channel: 'ad post',
        form_name: this._FORM_NAME_C2B_ORIGINS[this._origin] || this._FORM_NAME_C2B_ORIGINS.DEFAULT,
        page_category_level1: 'oferta instantanea',
        page_name: 'c2b cita confirmada',
        page_type: 'information',
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIODateConfirmed = event => {
    const c2bFlow = this._getC2bFlow()
    const eventName =
      this._origin === this._C2B_ORIGINS.INSTANT_OFFER_LANDING
        ? this._TRACKING_EVENT_NAMES.LANDING_IO_DATE_CONFIRMED
        : event

    const products = [...this._getAdProducts({adId: this._adId}), this._getDealerProduct({dealer: this._dealer})]

    const {day, hour} = this._getDateTrackingFormat({
      date: this._appointment.from
    })

    const eventData = {
      c2b_appointment_id: this._appointment.id,
      dealer_adress: this._dealer.location?.address,
      dealer_name: this._dealer.name,
      dealer_phone: this._dealer.phoneNumber,
      hit_information: this._whatsappConsentValue ? 'whatsapp activado' : 'whatsapp desactivado',
      hit_information2: this._dealer.name,
      io_id: this._ioId,
      io_price: this._instantOfferAppraisal,
      products,
      selected_date: day,
      selected_hour: hour,
      ...(c2bFlow && {c2b_flow: c2bFlow})
    }

    return {event: eventName, data: eventData}
  }

  mapAdInsertionIODateError = event => {
    const c2bFlow = this._getC2bFlow()
    const data = {page_error: this._errorType, ...(c2bFlow && {c2b_flow: c2bFlow})}

    if (this._errorType === this._formErrorTypes.fieldError) {
      const formFields = [!this._isDaySelected && 'dia', !this._isSlotSelected && 'hora'].filter(formField => formField)
      data.form_fields = formFields.join(':')
    }

    return {
      event,
      data
    }
  }

  mapAdInsertionIODatesViewed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        channel: 'ad post',
        page_category_level1: 'oferta instantanea',
        page_name: 'seleccion cita',
        page_type: 'form',
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIODealerOrderSelected = event => {
    const c2bFlow = this._getC2bFlow()

    return {
      event,
      data: {
        hit_information: this._hitInformation,
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIODealersViewed = event => {
    const c2bFlow = this._getC2bFlow()
    let stringPostalCode = this._postalCode.toString()
    if (stringPostalCode.length < 5) stringPostalCode = '0' + stringPostalCode

    const dealersProductsList = this._dealerList.map((dealer, index) =>
      this._getDealerProduct({dealer: dealer.dealer, listPosition: index + 1})
    )

    const products = [...this._getAdProducts({adId: this._adId}), ...dealersProductsList]

    const data = {
      channel: 'ad post',
      hit_information: stringPostalCode,
      page_category_level1: 'oferta instantanea',
      page_name: this._dealerList.length === 0 ? 'no dealers disponibles' : 'seleccion dealer',
      page_type: 'form',
      search_result_number: this._dealerList.length,
      products,
      ...(c2bFlow && {c2b_flow: c2bFlow})
    }

    return {event, data}
  }

  mapAdInsertionIOPostalCodeChanged = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        hit_origin: 'selector dealers',
        hit_information: this._hitInformation,
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIOFaqExpanded = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {hit_information: this._hitInformation, ...(c2bFlow && {c2b_flow: c2bFlow}), hit_origin: this._hitOrigin}
    }
  }

  mapAdInsertionIOModalViewed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        hit_information: this._hitInformation,
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIONoInteresaButtonClicked = event => {
    const c2bFlow = this._getC2bFlow()
    const eventData = {
      event
    }

    if (this._adId) {
      eventData.data = {
        ad_id: this._adId,
        products: this._getAdProducts({adId: this._adId}),
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    } else if (c2bFlow) {
      eventData.data = {c2b_flow: c2bFlow}
    }

    return eventData
  }

  mapAdInsertionIONoOfferAvailable = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        channel: 'ad post',
        page_category_level1: 'oferta instantanea',
        page_error: 'anuncio nuevo no target',
        page_name: 'no dealers disponibles',
        page_type: 'form',
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIOPublishAdClicked = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        hit_information: this._hitInformation,
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIOVideoPlayed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        hit_information: this._hitInformation,
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionIOWhatsappAction = event => {
    const c2bFlow = this._getC2bFlow()
    const data = c2bFlow ? {c2b_flow: c2bFlow} : undefined
    return {
      event: `Ad Insertion IO Whatsapp ${this._whatsappConsentValue ? 'Selected' : 'Deselected'}`,
      ...(data && {data})
    }
  }

  mapAdInsertionModalDismissed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        hit_information: this._HIT_INFORMATIONS.HOW_VEHICLE_VALUE_IS_CALCULATED,
        products: this._getAdProducts({adId: this._adId}),
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapAdInsertionModalViewed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        hit_information: this._HIT_INFORMATIONS.HOW_VEHICLE_VALUE_IS_CALCULATED,
        products: this._getAdProducts({adId: this._adId}),
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  mapInstantOfferFieldSelected = () => {
    const c2bFlow = this._getC2bFlow()
    const data = c2bFlow ? {c2b_flow: c2bFlow} : undefined
    return {
      event: `Instant Offer ${this._fieldNameToTrackingIds[this._fieldId]?.field ?? `Field`} Selected`,
      ...(data && {data})
    }
  }

  mapInstantOfferModalViewed = event => {
    const c2bFlow = this._getC2bFlow()
    return {
      event,
      data: {
        hit_information: this._hitInformation,
        ...(c2bFlow && {c2b_flow: c2bFlow})
      }
    }
  }

  map(eventName) {
    const instantOfferMappers = {
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_ADD_TO_CALENDAR]: this.mapAdInsertionIOAddToCalendar,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_BUTTON_CLICKED]: this.mapAdInsertionIOButtonClicked,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_CAR_DETAILS_ERROR]: this.mapAdInsertionIOCarDetailsError,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_CAR_DETAILS_SUCCESS]: this.mapAdInsertionIOCarDetailsSuccess,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_CAR_DETAILS_VIEWED]: this.mapAdInsertionIOCarDetailsViewed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_CONFIRMATION_FAQ_EXPANDED]:
        this.mapAdInsertionIOConfirmationFaqExpanded,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_CONFIRMATION_VIEWED]: this.mapAdInsertionIOConfirmationViewed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_DATE_CONFIRMED]: this.mapAdInsertionIODateConfirmed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_DATE_ERROR]: this.mapAdInsertionIODateError,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_DATES_VIEWED]: this.mapAdInsertionIODatesViewed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_DEALER_ORDER_SELECTED]: this.mapAdInsertionIODealerOrderSelected,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_DEALERS_VIEWED]: this.mapAdInsertionIODealersViewed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_FAQ_EXPANDED]: this.mapAdInsertionIOFaqExpanded,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_MODAL_VIEWED]: this.mapAdInsertionIOModalViewed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_NO_INTERESA_BUTTON_CLICKED]:
        this.mapAdInsertionIONoInteresaButtonClicked,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_NO_OFFER_AVAILABLE]: this.mapAdInsertionIONoOfferAvailable,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_POSTAL_CODE_CHANGED]: this.mapAdInsertionIOPostalCodeChanged,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_PUBLISH_AD_CLICKED]: this.mapAdInsertionIOPublishAdClicked,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_VIDEO_PLAYED]: this.mapAdInsertionIOVideoPlayed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_IO_WHATSAPP_ACTION]: this.mapAdInsertionIOWhatsappAction,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_MODAL_DISMISSED]: this.mapAdInsertionModalDismissed,
      [this._TRACKING_EVENT_NAMES.AD_INSERTION_MODAL_VIEWED]: this.mapAdInsertionModalViewed,
      [this._TRACKING_EVENT_NAMES.INSTANT_OFFER_FIELD_SELECTED]: this.mapInstantOfferFieldSelected,
      [this._TRACKING_EVENT_NAMES.INSTANT_OFFER_MODAL_VIEWED]: this.mapInstantOfferModalViewed
    }

    return instantOfferMappers[eventName] ? instantOfferMappers[eventName](eventName) : {event: eventName}
  }
}
